import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";

export function PaymentSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("timeout"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBooking(data.booking || null);
          setState("paid");
          return;
        }
      } catch (e) { /* keep polling */ }
      if (attempts >= 15) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory p-4" data-testid="payment-success-page">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto text-fir animate-spin" size={56} />
            <h1 className="font-display text-3xl text-fir mt-5">{t("Verifica del pagamento in corso…")}</h1>
            <p className="text-fir-deep/70 mt-2">{t("Attendi qualche istante, stiamo confermando la tua caparra.")}</p>
          </>
        )}
        {state === "paid" && (
          <div data-testid="payment-success-confirmed">
            <CheckCircle2 className="mx-auto text-gold" size={64} />
            <h1 className="font-display text-3xl text-fir mt-4">{t("Prenotazione confermata!")}</h1>
            {booking && (
              <div className="mt-5 rounded-2xl bg-fir/5 p-5 text-left text-sm space-y-2">
                <div className="flex justify-between"><span className="text-fir-deep/70">{t("Struttura")}</span><span className="font-medium text-fir">{booking.apartment_title}</span></div>
                <div className="flex justify-between"><span className="text-fir-deep/70">{t("Ospite")}</span><span className="font-medium text-fir">{booking.guest_name}</span></div>
                <div className="flex justify-between"><span className="text-fir-deep/70">{t("Periodo")}</span><span className="font-medium text-fir">{booking.checkin} → {booking.checkout}</span></div>
                <div className="flex justify-between border-t border-stone pt-2 mt-2"><span className="text-fir-deep/70">{t("Caparra versata (20%)")}</span><span className="font-bold text-fir">€{booking.deposit}</span></div>
                <div className="flex justify-between"><span className="text-fir-deep/70">{t("Saldo all'arrivo")}</span><span className="font-medium text-fir">€{booking.balance_due}</span></div>
              </div>
            )}
            <p className="text-fir-deep/70 mt-4">{t("Ti abbiamo inviato una email di conferma. Non vediamo l'ora di accoglierti a Dimaro!")}</p>
            <Link to="/" data-testid="payment-success-home-btn" className="inline-block mt-6 gold-btn px-8 py-3 rounded-xl">{t("Torna alla home")}</Link>
          </div>
        )}
        {state === "timeout" && (
          <div data-testid="payment-success-timeout">
            <Loader2 className="mx-auto text-wood" size={48} />
            <h1 className="font-display text-2xl text-fir mt-4">{t("Stiamo ancora verificando il pagamento")}</h1>
            <p className="text-fir-deep/70 mt-2">{t("Se hai completato il pagamento, riceverai a breve l'email di conferma. In caso di dubbi contattaci pure.")}</p>
            <Link to="/" className="inline-block mt-6 gold-btn px-8 py-3 rounded-xl">{t("Torna alla home")}</Link>
          </div>
        )}
      </div>
      <div className="fixed bottom-4 inset-x-0 text-center text-xs text-fir-deep/50 flex justify-center gap-4">
        <Link to="/privacy" className="hover:text-gold">Privacy</Link>
        <Link to="/cookie-policy" className="hover:text-gold">Cookie Policy</Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory p-4" data-testid="payment-cancel-page">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 text-center">
        <XCircle className="mx-auto text-red-400" size={64} />
        <h1 className="font-display text-3xl text-fir mt-4">{t("Pagamento annullato")}</h1>
        <p className="text-fir-deep/70 mt-2">{t("Nessun addebito è stato effettuato. Puoi riprovare la prenotazione quando vuoi.")}</p>
        <Link to="/" data-testid="payment-cancel-home-btn" className="inline-block mt-6 gold-btn px-8 py-3 rounded-xl">{t("Torna alla home")}</Link>
      </div>
      <div className="fixed bottom-4 inset-x-0 text-center text-xs text-fir-deep/50 flex justify-center gap-4">
        <Link to="/privacy" className="hover:text-gold">Privacy</Link>
        <Link to="/cookie-policy" className="hover:text-gold">Cookie Policy</Link>
      </div>
    </div>
  );
}
